import { Button, Pagination, Spinner } from '~/ui';
import { t } from 'i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useEffect, useRef, useState } from 'react';
import SelectedIcon from '../ImageArtickeContainer/icoms/selected-icon.svg?react';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import styles from './ImageQuillContainer.module.scss';
import classNames from 'classnames';

interface IImage {
  id: number;
  full_photo_url: string;
  path: string;
}

interface IImageQuillProps {
  assetId: number;
  isOpen: boolean;
  onClose: () => void;
  onInsertImage: (imageUrl: string[]) => void;
}

const ImageQuillContainer = ({
  assetId,
  isOpen,
  onClose,
  onInsertImage,
}: IImageQuillProps) => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<IImage[]>([]);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<IImage[]>([]);

  const { data: images, isLoading } = useQuery({
    queryFn: async () => {
      if (!assetId) return { data: [], last_page: 1 };
      const { data } = await instance.get(
        `admin/photo-storage/knowledge_base/${assetId}`,
        { params: { perPage: 9, page } }
      );
      return data;
    },
    queryKey: ['kb-quill-images', assetId, page],
    onError: error => toastError(error),
    enabled: isOpen && !!assetId,
  });

  const { mutateAsync: uploadPhotos } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('photos[]', file));
      const { data } = await instance.post('admin/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as IImage[];
    },
    { onError: error => toastError(error) }
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      const uploaded = await uploadPhotos(Array.from(files));
      if (uploaded?.length) {
        setUploadedPhotos(prev => [...uploaded.map(p => ({ ...p, path: '' })), ...prev]);
        toastSuccess(t('photo_uploaded'));
      }
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Маппинг photo_storage → формат IImage
  const storageImages: IImage[] = (images?.data || []).map(
    (photo: { id: number; photo_id: number; url: string }) => ({
      id: photo.photo_id,
      full_photo_url: photo.url,
      path: '',
    })
  );

  const uploadedIds = new Set(uploadedPhotos.map(p => p.id));
  const mappedImages = [...uploadedPhotos, ...storageImages.filter(p => !uploadedIds.has(p.id))];

  const handleSubmit = () => {
    if (!!selectedId.length) {
      const imageUrl = selectedId.map(item => item.full_photo_url);
      onInsertImage(imageUrl);
      onClose();
    }
  };

  const handleSelectImage = (item: IImage) => {
    setSelectedId(prev =>
      prev.some(selected => selected.id === item.id)
        ? prev.filter(selected => selected.id !== item.id)
        : [...prev, item]
    );
  };

  useEffect(() => {
    if (!isOpen) setSelectedId([]);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.photoContainer}>
      <div className={styles.headingWrapper}>
        <p className={styles.title}>{t('choose_photo')}</p>
        <div className={styles.closeBtnWrapper}>
          {!!selectedId.length && (
            <Button
              text={t('discard')}
              className={styles.resetBtn}
              onClick={() => setSelectedId([])}
            />
          )}
          <Button
            text={t('close')}
            className={styles.closeBtn}
            onClick={onClose}
          />
        </div>
      </div>
      <div className={styles.uploadSection}>
        <button
          type="button"
          className={styles.uploadButton}
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Spinner />
          ) : (
            <>
              <PhotoIcon />
              <span>{t('upload_photos')}</span>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className={styles.hiddenInput}
        />
      </div>
      <div className={styles.imagesWrapper}>
        <ul className={!isLoading ? styles.imagesList : styles.loadingList}>
          {isLoading ? (
            <Spinner />
          ) : mappedImages.length ? (
            mappedImages.map((item: IImage) => (
              <li key={item.id} className={styles.imagesItem}>
                <button
                  type="button"
                  onClick={() => handleSelectImage(item)}
                  className={classNames(
                    styles.imageBtn,
                    selectedId.some(selected => selected.id === item.id) &&
                      styles.activeImage
                  )}
                >
                  <img
                    src={item.full_photo_url}
                    alt={item.path}
                    className={styles.image}
                  />
                  {selectedId.some(selected => selected.id === item.id) && (
                    <SelectedIcon className={styles.selectedIcon} />
                  )}
                </button>
              </li>
            ))
          ) : (
            <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>{t('no_photos')}</p>
          )}
        </ul>
        <Pagination
          pageCount={images?.last_page}
          onPageChange={(selectedPage: { selected: number }) =>
            setPage(selectedPage.selected + 1)
          }
        />
      </div>
      <Button
        text={t('confirm')}
        className={styles.submitBtn}
        onClick={handleSubmit}
        disabled={!selectedId.length}
      />
    </div>
  );
};

export default ImageQuillContainer;
